import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { openFinanceSlideout } from '../../actions/lister-actions';
import FinanceWithChevron from './FinanceWithChevron';
import switch24Pill from '../../assets/switch24enhanced.svg';
import { useSwitch24DataHook } from '../../hooks/useSwitch24Data';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';
import useAprExamples from '../../hooks/useAprExamples';
import {
	getFinanceButtonConfig,
	buildSlideoutPayload,
} from '../../utilities/finance-utilities';

const FinancePropositionEnhanced = ({
	NewDayFinanceQuote,
	Promotions = {},
	PricePodViewModel: { NowPrice, MemberPrice } = {},
	UpfrontPricing = {
		HasData: false,
		HasMemberData: false,
	},
	classes,
	Code,
	ColourId,
	State,
	Switch24Pricing,
}) => {
	const FinancePromotions = Promotions?.FinancePromotions ?? [];
	const dispatch = useDispatch();

	const {
		AnnualMembershipFee,
		UseUpfrontPricingAmounts,
		UseSwitch24,
		LowInterestFinancePromotionsEnabled,
	} = useSelector((state) => state.featureSwitches);

	const { aprRatesForProduct } = useAprExamples();
	const { aprRepresentative, aprPurchaseRate } = aprRatesForProduct(ColourId);

	const switch24Props = { UseSwitch24, Switch24Pricing };
	const isSwitch24Product = useSwitch24DataHook(switch24Props);

	const financePromotion = FinancePromotions?.[0] ?? null;
	const financePromotionPillConfig = getFinanceButtonConfig(financePromotion);
	const PromotionType = financePromotion?.PromotionType || '';
	const PromotionMonths = financePromotion?.PromotionMonths || 0;
	const hasInstalmentPlan =
		NewDayFinanceQuote?.PriceIsLessThanMinimum === false;
	const hasFinancePromotion = !!financePromotion?.PromotionType;
	const isLowInterestFinancePromo =
		financePromotion?.PromotionType === 'FourPointNinePercent' ||
		financePromotion?.PromotionType === 'NinePointNinePercent';

	const financeProps = {
		NewDayFinanceQuote,
		UpfrontPricing,
		MemberPrice,
		NowPrice,
		Code,
		ColourId,
		PromotionType,
		PromotionMonths,
		FinancePromotions,
		State,
		Switch24Pricing,
		isSwitch24Product,
		representativeAprRateString: aprRepresentative,
		purchaseAprRateString: aprPurchaseRate,
		useEnhancedFinanceProposition: true,
		isFinancePromotion: hasFinancePromotion,
	};

	const slideoutPayload = buildSlideoutPayload({
		...financeProps,
		AnnualMembershipFee,
		UseUpfrontPricingAmounts,
		UseSwitch24,
	});

	const intersectionPayload = {
		type: 'TrackIntersection',
		name: 'Switch24 Product',
		section: 'Switch24 Finance Section',
	};

	const intersectionRef = useIntersectionObserver(intersectionPayload);

	const openFinanceSlideoutWithPayload = () => {
		dispatch(openFinanceSlideout(slideoutPayload));
	};

	const renderSwitch24Pill = () => (
		<div
			className="relative"
			ref={intersectionRef}
			data-tag-name="finance-switch-24-button"
			data-testid="finance-switch-24"
			data-tag-section="Switch24 finance section">
			<div className="relative">
				<FinanceWithChevron {...financeProps} />
				<img
					src={switch24Pill}
					alt="switch24PromoPill"
					className="absolute right-0 top-0"
					style={{ width: '54px' }}
					data-testid="switch-24-pill"
				/>
			</div>
		</div>
	);

	const renderFinancePromotionPill = () => (
		<div className="mt-4">
			<div className="text-title-sm">AO Finance</div>

			<button
				type="button"
				aria-label={financePromotionPillConfig.label}
				className={`${classes} relative z-10 p-0 mt-2 bg-transparent`}
				onClick={openFinanceSlideoutWithPayload}
				data-testid={financePromotionPillConfig.testId}>
				<div className="inline-flex items-center bg-steam p-1 rounded-sm text-title-sm text-xs">
					<span className="px-2">{financePromotionPillConfig.promoText}</span>
				</div>
			</button>

			<FinanceWithChevron {...financeProps} />
		</div>
	);

	const renderInstalmentPlan = () => (
		<div className="mt-4">
			<div className="text-title-sm">AO Finance</div>
			<FinanceWithChevron {...financeProps} />
		</div>
	);

	if (isSwitch24Product) {
		return renderSwitch24Pill();
	}

	if (
		(hasFinancePromotion && !isLowInterestFinancePromo) ||
		(hasFinancePromotion && LowInterestFinancePromotionsEnabled)
	) {
		return renderFinancePromotionPill();
	}

	if (hasInstalmentPlan) {
		return renderInstalmentPlan();
	}

	return (
		<button
			type="button"
			aria-label="Flexible Credit Finance Promotion"
			className={`${classes} relative z-10 p-0 w-full mt-2 bg-transparent`}
			onClick={openFinanceSlideoutWithPayload}
			data-tag-name="finance-flexible-credit-button"
			data-testid="finance-flexible-credit">
			<div className="flex flex-row items-center bg-white border border-gray-30 border-solid p-2 rounded text-xs text-left">
				<i className="ico ico-finance-gbp text-xl text-cta-main" />
				<span className="px-2 flex-grow text-gray-100">
					AO Finance available
				</span>
				<i className="ico ico-chevron-right text-xl text-cta-action" />
			</div>
		</button>
	);
};

export default FinancePropositionEnhanced;
